import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle2, Tv, ArrowLeft, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Alphabet used by backend `_generate_tv_code` (no I, L, O, 0, 1).
const TV_CODE_CHARS = /[ABCDEFGHJKMNPQRSTUVWXYZ23456789]/;

/**
 * TVPairPage — the phone-side page a signed-in subscriber opens after seeing
 * a 6-char code on their smart TV (`/tv` page). They type it in, we hit
 * `POST /api/tv/pair/claim`, backend flips the code to `claimed=true` and
 * the TV starts streaming on its next poll.
 */
const TVPairPage = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefill = (searchParams.get('code') || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);

  const [code, setCode] = useState(prefill);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Kick unauthenticated users to /login and come back here — preserve the
  // `?code=XXXXXX` query param so the QR-scan flow doesn't lose it during login.
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      const returnTo = `/tv/pair${typeof window !== 'undefined' ? window.location.search : ''}`;
      navigate(`/login?redirect=${encodeURIComponent(returnTo)}`);
    }
  }, [authLoading, isAuthenticated, navigate]);

  const canWatch = useMemo(() => {
    if (!user) return false;
    return (
      user.role === 'admin' ||
      user.role === 'coach' ||
      user.is_stream_manager ||
      user.subscription_status === 'active' ||
      user.subscription_status === 'trial'
    );
  }, [user]);

  const handleChange = (raw) => {
    const cleaned = (raw || '')
      .toUpperCase()
      .split('')
      .filter((c) => TV_CODE_CHARS.test(c))
      .join('')
      .slice(0, 6);
    setCode(cleaned);
    if (error) setError('');
  };

  const submitClaim = useCallback(async (codeToClaim) => {
    setError('');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/tv/pair/claim`, { code: codeToClaim });
      if (r.data?.claimed) {
        setSuccess(true);
      } else {
        setError('Could not pair. Try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not pair this code.');
    } finally {
      setSubmitting(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Enter the 6-character code shown on your TV.');
      return;
    }
    await submitClaim(code);
  };

  // QR-scan flow: if a valid code was passed via `?code=XXXXXX` AND the user
  // is a paid subscriber, auto-submit the claim so they never have to tap.
  const autoTriedRef = useRef(false);
  useEffect(() => {
    if (autoTriedRef.current) return;
    if (authLoading || !isAuthenticated) return;
    if (!prefill || prefill.length !== 6) return;
    if (!canWatch) return;
    if (submitting || success) return;
    autoTriedRef.current = true;
    submitClaim(prefill);
  }, [authLoading, isAuthenticated, prefill, canWatch, submitting, success, submitClaim]);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-8 md:py-16">
      <div className="max-w-md mx-auto">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6"
          data-testid="tv-pair-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <Card className="border-white/10 bg-black/40 backdrop-blur">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
                <Tv className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl font-black uppercase tracking-tight">
                  Pair Your TV
                </CardTitle>
                <CardDescription className="text-white/60">
                  Sign in on the big screen, no remote typing.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {success ? (
              <div className="text-center py-6 space-y-4" data-testid="tv-pair-success">
                <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />
                <div>
                  <div className="text-2xl font-bold mb-1">TV Paired</div>
                  <p className="text-white/70 text-sm">
                    Look at your TV — the live stream will start automatically.
                    If nothing plays, no live event is running right now; it&apos;ll
                    start the next time one goes live.
                  </p>
                </div>
                <div className="flex flex-col gap-2 pt-2">
                  <Button
                    onClick={() => { setSuccess(false); setCode(''); autoTriedRef.current = true; }}
                    variant="outline"
                    className="border-white/20"
                    data-testid="tv-pair-another"
                  >
                    Pair another TV
                  </Button>
                  <Button
                    onClick={() => navigate('/events')}
                    className="bg-primary text-primary-foreground"
                    data-testid="tv-pair-done"
                  >
                    Watch here too
                  </Button>
                </div>
              </div>
            ) : (prefill && submitting) ? (
              <div className="text-center py-8 space-y-4" data-testid="tv-pair-auto-loading">
                <Loader2 className="w-14 h-14 text-primary animate-spin mx-auto" />
                <div>
                  <div className="text-xl font-bold mb-1">Pairing your TV…</div>
                  <p className="text-white/60 text-sm">
                    Code <span className="font-mono text-white">{prefill}</span> — hang tight.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <ol className="text-sm text-white/70 space-y-1 list-decimal list-inside">
                  <li>
                    On your TV browser, go to{' '}
                    <span className="font-mono text-white">elevatedarena.com/tv</span>
                  </li>
                  <li>Type the 6-character code you see below</li>
                  <li>Watch live 🎥</li>
                </ol>

                {!canWatch && (
                  <Alert className="border-yellow-500/40 bg-yellow-500/10">
                    <AlertDescription className="text-yellow-200 text-sm">
                      You need an active subscription to pair a TV.{' '}
                      <Link to="/subscription" className="underline font-semibold">
                        Subscribe
                      </Link>
                    </AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="tv-code" className="block text-xs uppercase tracking-widest text-white/50 mb-2">
                      TV Code
                    </label>
                    <input
                      id="tv-code"
                      data-testid="tv-pair-code-input"
                      type="text"
                      inputMode="text"
                      autoCapitalize="characters"
                      autoComplete="off"
                      spellCheck={false}
                      value={code}
                      onChange={(e) => handleChange(e.target.value)}
                      placeholder="XXXXXX"
                      maxLength={6}
                      disabled={!canWatch || submitting}
                      className="w-full text-center font-mono font-black text-4xl md:text-5xl tracking-[0.4em] bg-white/5 border-2 border-white/20 focus:border-primary focus:outline-none rounded-xl py-4 uppercase disabled:opacity-40"
                    />
                  </div>

                  {error && (
                    <Alert className="border-red-500/40 bg-red-500/10">
                      <AlertDescription className="text-red-200 text-sm" data-testid="tv-pair-error">
                        {error}
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    disabled={!canWatch || submitting || code.length !== 6}
                    className="w-full bg-primary text-primary-foreground font-bold text-lg py-6"
                    data-testid="tv-pair-submit"
                  >
                    {submitting ? (
                      <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Pairing…</>
                    ) : (
                      'Pair TV'
                    )}
                  </Button>
                </form>

                <div className="text-xs text-white/40 text-center pt-2 border-t border-white/10">
                  Signed in as <span className="text-white/70">{user?.email}</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TVPairPage;
